import { HISTORY_ACTION_MODE, NodeId } from '@candulabs/craft';
import { useMemo } from 'react';

import { useMultiNodes } from 'craft/hooks/useMultiNodes';
import {
  useIsUsingDefaultCssControls,
  useViewportBreakpoint,
} from 'craft/state/viewport/selectors';
import { MediaBreakpoint } from 'craft/types';

export const useResponsiveProps = (nodeIds: NodeId[], commonProperty?: any) => {
  const viewportBreakpoint = useViewportBreakpoint();
  const isUsingDefaultCssControls = useIsUsingDefaultCssControls();
  const {
    props,
    media,
    actions: { setProps, setCustom },
  } = useMultiNodes(
    nodeIds,
    (node) => ({
      props: node?.data.props ?? {},
      media: node?.data.custom?.media ?? {},
    }),
    commonProperty,
  );

  const responsiveProps = useMemo(() => {
    if (viewportBreakpoint === MediaBreakpoint.LG) {
      return props;
    }
    const mediaProps = media?.props ?? {};
    if (viewportBreakpoint === MediaBreakpoint.MD) {
      return { ...(props ?? {}), ...(mediaProps[MediaBreakpoint.MD] ?? {}) };
    }
    return {
      ...(props ?? {}),
      ...(mediaProps[MediaBreakpoint.MD] ?? {}),
      ...(mediaProps[MediaBreakpoint.SM] ?? {}),
    };
  }, [props, media, viewportBreakpoint]);

  const setResponsiveProps = (
    newProps: object,
    historyMode?: HISTORY_ACTION_MODE,
    throttleNumber?: number,
  ) => {
    if (isUsingDefaultCssControls) {
      setProps(newProps, historyMode, throttleNumber);
    } else {
      setCustom(
        {
          media: {
            ...(media || {}),
            props: {
              ...(media.props || {}),
              [viewportBreakpoint]: {
                ...((media.props || {})[viewportBreakpoint] ?? {}),
                ...(newProps ?? {}),
              },
            },
          },
        },
        historyMode,
        throttleNumber,
      );
    }
  };

  return { responsiveProps, setResponsiveProps };
};
